import { Injectable, inject } from '@angular/core';
import { UserScopedStorageService } from '../services/user-scoped-storage.service';

const TTL_MS = 24 * 60 * 60 * 1000;

export interface CollectionNavCursor {
  collectionKey: string;
  orderedIds: string[];
  filterSnapshot?: unknown;
  sortKey?: string;
  sortDirection?: string;
  updatedAt: number;
}

@Injectable({ providedIn: 'root' })
export class CollectionNavigationService {
  private readonly userStorage = inject(UserScopedStorageService);

  private storageKey(collectionKey: string): string {
    return `${collectionKey}-nav-cursor`;
  }

  writeCursor(
    collectionKey: string,
    orderedIds: string[],
    opts?: { filterSnapshot?: unknown; sortKey?: string; sortDirection?: string }
  ): void {
    const cursor: CollectionNavCursor = {
      collectionKey,
      orderedIds,
      filterSnapshot: opts?.filterSnapshot,
      sortKey: opts?.sortKey,
      sortDirection: opts?.sortDirection,
      updatedAt: Date.now(),
    };
    this.userStorage.set(this.storageKey(collectionKey), JSON.stringify(cursor));
  }

  readCursor(collectionKey: string): CollectionNavCursor | null {
    const raw = this.userStorage.get(this.storageKey(collectionKey));
    if (!raw) return null;

    try {
      const cursor = JSON.parse(raw) as CollectionNavCursor;

      if (Date.now() - cursor.updatedAt > TTL_MS) {
        this.userStorage.remove(this.storageKey(collectionKey));
        return null;
      }

      return cursor;
    } catch {
      return null;
    }
  }

  clearCursor(collectionKey: string): void {
    this.userStorage.remove(this.storageKey(collectionKey));
  }

  getNeighbours(
    collectionKey: string,
    currentId: string
  ): { prev: string | null; next: string | null; index: number; total: number } | null {
    const cursor = this.readCursor(collectionKey);
    if (!cursor) return null;

    const idx = cursor.orderedIds.indexOf(currentId);
    if (idx < 0) return null;

    return {
      prev: idx > 0 ? cursor.orderedIds[idx - 1] : null,
      next: idx < cursor.orderedIds.length - 1 ? cursor.orderedIds[idx + 1] : null,
      index: idx + 1,
      total: cursor.orderedIds.length,
    };
  }
}
